import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  Plus, Search, X, Loader2, Building2, Phone, Mail, Globe, MapPin, User,
  Calendar, CalendarClock, Users, MessageSquare, Trash2, ArrowUpRight,
  CheckCircle2, Star, ShieldQuestion, PhoneCall, Target,
} from "lucide-react";

const STAGES = ["not_contacted", "initial_contact", "identifying_dm", "awaiting_response",
  "follow_up", "meeting_scheduled", "qualified", "not_interested"];
const STAGE_LABELS = {
  not_contacted: "Not Contacted", initial_contact: "Initial Contact Made",
  identifying_dm: "Identifying Decision-Maker", awaiting_response: "Awaiting Response",
  follow_up: "Follow-Up Needed", meeting_scheduled: "Meeting Scheduled",
  qualified: "Qualified Lead", not_interested: "Not Interested / Do Not Contact",
};
const STAGE_TONE = {
  not_contacted: "bg-black/6 text-black/55", initial_contact: "bg-[#c9a961]/18 text-[#a3823f]",
  identifying_dm: "bg-[#5b7c99]/15 text-[#3d5a73]", awaiting_response: "bg-[#8a6d99]/15 text-[#5f4670]",
  follow_up: "bg-[#c9a961]/25 text-[#8a6a28]", meeting_scheduled: "bg-[#5b7f6b]/18 text-[#3b5a48]",
  qualified: "bg-[#6b7c3a]/18 text-[#4d5a24]", not_interested: "bg-[#b23a48]/10 text-[#b23a48]",
};
const REVENUE = [
  ["unknown", "Unknown"], ["lt_10k", "Under $10K/mo"], ["10k_25k", "$10K–$25K/mo"],
  ["25k_50k", "$25K–$50K/mo"], ["50k_100k", "$50K–$100K/mo"], ["100k_250k", "$100K–$250K/mo"],
  ["250k_500k", "$250K–$500K/mo"], ["gt_500k", "$500K+/mo"],
];
const REV_LABEL = Object.fromEntries(REVENUE);
const HEALTH = [
  ["not_assessed", "Not Assessed"], ["poor", "Poor"], ["fair", "Fair"],
  ["good", "Good"], ["strong", "Strong"], ["excellent", "Excellent"],
];
const HEALTH_LABEL = Object.fromEntries(HEALTH);

const _toLocal = (iso) => {
  if (!iso) return null;
  const s = String(iso);
  // date-only strings → parse as LOCAL midnight (avoid UTC off-by-one)
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + "T00:00:00") : new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const fmtDate = (iso) => { const d = _toLocal(iso); return d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""; };
const fmtWhen = (iso) => { const d = _toLocal(iso); return d ? d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""; };
const isOverdue = (iso) => { const d = _toLocal(iso); if (!d) return false; const today = new Date(); today.setHours(0, 0, 0, 0); return d < today; };

const inputCls = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none";
const labelCls = "block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1";

export default function OutreachContacts({ onLeadsChanged }) {
  const [companies, setCompanies] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [view, setView] = useState("cards");
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: t }] = await Promise.all([
        api.get("/admin/outreach/companies"),
        api.get("/admin/outreach/team"),
      ]);
      setCompanies(data.companies || []);
      setTeam(t.members || []);
    } catch { toast.error("Couldn't load Outreach Contacts."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upsertLocal = useCallback((c) => {
    setCompanies((list) => { const i = list.findIndex((x) => x.id === c.id); if (i === -1) return [c, ...list]; const cp = [...list]; cp[i] = c; return cp; });
  }, []);

  const addCompany = async () => {
    const name = (window.prompt("Company name:", "") || "").trim();
    if (!name) return;
    setAdding(true);
    try {
      const { data } = await api.post("/admin/outreach/companies", { name });
      upsertLocal(data);
      setOpenId(data.id);
    } catch { toast.error("Couldn't add the company."); }
    finally { setAdding(false); }
  };

  const removeLocal = (id) => setCompanies((l) => l.filter((x) => x.id !== id));

  const moveStage = useCallback(async (id, stage) => {
    setCompanies((list) => {
      const prev = list.find((c) => c.id === id);
      if (!prev || prev.stage === stage) return list;
      const cp = list.map((c) => (c.id === id ? { ...c, stage } : c));
      api.put(`/admin/outreach/companies/${id}`, { stage })
        .then(({ data }) => setCompanies((l2) => l2.map((c) => (c.id === id ? data : c))))
        .catch(() => { setCompanies((l2) => l2.map((c) => (c.id === id ? prev : c))); toast.error("Couldn't move the card."); });
      return cp;
    });
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return companies.filter((c) => {
      if (stageFilter && c.stage !== stageFilter) return false;
      if (!term) return true;
      return (c.name || "").toLowerCase().includes(term)
        || (c.industry || "").toLowerCase().includes(term)
        || (c.contacts || []).some((k) => (k.name || "").toLowerCase().includes(term) || (k.email || "").toLowerCase().includes(term));
    });
  }, [companies, q, stageFilter]);

  const counts = useMemo(() => {
    const m = {}; companies.forEach((c) => { m[c.stage] = (m[c.stage] || 0) + 1; }); return m;
  }, [companies]);

  const open = companies.find((c) => c.id === openId);

  return (
    <div data-testid="tab-content-outreach-contacts" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#1e3d3a]/35" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search companies or contacts…"
            data-testid="outreach-search" className={`${inputCls} pl-9`} />
        </div>
        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="outreach-stage-filter"
          className="rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none">
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
        </select>
        <div className="inline-flex rounded-lg border border-[#1e3d3a]/15 bg-white p-0.5" data-testid="outreach-view-toggle">
          <button onClick={() => setView("cards")} data-testid="outreach-view-cards"
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === "cards" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#1e3d3a]"}`}>Cards</button>
          <button onClick={() => { setView("board"); setStageFilter(""); }} data-testid="outreach-view-board"
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === "board" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#1e3d3a]"}`}>Board</button>
        </div>
        <button onClick={addCompany} disabled={adding} data-testid="outreach-add-company"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add company
        </button>
      </div>

      {view === "cards" && (
        <div className="flex flex-wrap gap-1.5" data-testid="outreach-stage-chips">
          {STAGES.filter((s) => counts[s]).map((s) => (
            <button key={s} onClick={() => setStageFilter(stageFilter === s ? "" : s)}
              className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full ${STAGE_TONE[s]} ${stageFilter === s ? "ring-1 ring-[#1e3d3a]/30" : ""}`}>
              <span className="tabular-nums font-semibold">{counts[s]}</span> {STAGE_LABELS[s]}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-[#1e3d3a]/50 py-16 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : view === "board" ? (
        <BoardView companies={filtered} onOpen={setOpenId} onMove={moveStage}
          dragId={dragId} setDragId={setDragId} overStage={overStage} setOverStage={setOverStage} />
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#1e3d3a]/15 bg-white/60 p-12 text-center" data-testid="outreach-empty">
          <Building2 className="h-10 w-10 mx-auto text-[#1e3d3a]/25" />
          <p className="text-[#1e3d3a]/60 mt-3 text-sm">{companies.length === 0 ? "No companies yet. Add the first business you're approaching." : "No companies match your filters."}</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="outreach-company-grid">
          {filtered.map((c) => {
            const dm = (c.contacts || []).find((k) => k.is_decision_maker);
            return (
              <button key={c.id} onClick={() => setOpenId(c.id)} data-testid={`outreach-card-${c.id}`}
                className="text-left rounded-xl border border-[#1e3d3a]/10 bg-white p-4 hover:border-[#c9a961] hover:shadow-sm transition-all">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-head text-[#1e3d3a] truncate">{c.name}</h3>
                    <p className="text-xs text-[#1e3d3a]/50 truncate">{c.industry || "—"}</p>
                  </div>
                  {c.converted_lead_id && <span title="In Leads" className="shrink-0 inline-flex items-center gap-1 text-[10px] text-[#4d5a24] bg-[#6b7c3a]/15 px-2 py-0.5 rounded-full"><ArrowUpRight className="h-3 w-3" /> Lead</span>}
                </div>
                <span className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full ${STAGE_TONE[c.stage]}`}>{STAGE_LABELS[c.stage]}</span>
                <div className="mt-3 space-y-1 text-xs text-[#1e3d3a]/60">
                  <div className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {(c.contacts || []).length} contact{(c.contacts || []).length === 1 ? "" : "s"}{dm ? ` · DM: ${dm.name}` : ""}</div>
                  {c.next_followup_at && <div className={`flex items-center gap-1.5 ${isOverdue(c.next_followup_at) ? "text-[#b23a48] font-medium" : ""}`}><CalendarClock className="h-3.5 w-3.5" /> Follow-up {fmtDate(c.next_followup_at)}{isOverdue(c.next_followup_at) ? " · overdue" : ""}</div>}
                  {c.next_step && <div className="flex items-center gap-1.5 truncate"><Target className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{c.next_step}</span></div>}
                  {c.next_step_owner_name && <div className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {c.next_step_owner_name}</div>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {open && (
        <CompanyModal company={open} team={team} onClose={() => setOpenId(null)}
          onChange={upsertLocal} onDeleted={() => { removeLocal(open.id); setOpenId(null); }}
          onLeadsChanged={onLeadsChanged} />
      )}
    </div>
  );
}

function BoardView({ companies, onOpen, onMove, dragId, setDragId, overStage, setOverStage }) {
  const byStage = (s) => companies.filter((c) => c.stage === s);
  return (
    <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1" data-testid="outreach-board">
      {STAGES.map((s) => {
        const items = byStage(s);
        return (
          <div key={s} data-testid={`outreach-board-col-${s}`}
            onDragOver={(e) => { e.preventDefault(); if (overStage !== s) setOverStage(s); }}
            onDragLeave={() => setOverStage((cur) => (cur === s ? null : cur))}
            onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain") || dragId; if (id) onMove(id, s); setOverStage(null); setDragId(null); }}
            className={`shrink-0 w-[250px] rounded-xl border p-2 transition-colors ${overStage === s ? "border-[#c9a961] bg-[#c9a961]/8" : "border-[#1e3d3a]/10 bg-[#1e3d3a]/[0.02]"}`}>
            <div className="flex items-center justify-between px-1 py-1.5">
              <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${STAGE_TONE[s]}`}>{STAGE_LABELS[s]}</span>
              <span className="text-xs text-[#1e3d3a]/45 tabular-nums">{items.length}</span>
            </div>
            <div className="space-y-2 min-h-[40px]">
              {items.map((c) => {
                const dm = (c.contacts || []).find((k) => k.is_decision_maker);
                return (
                  <div key={c.id} draggable data-testid={`outreach-board-card-${c.id}`}
                    onDragStart={(e) => { e.dataTransfer.setData("text/plain", c.id); e.dataTransfer.effectAllowed = "move"; setDragId(c.id); }}
                    onDragEnd={() => { setDragId(null); setOverStage(null); }}
                    onClick={() => onOpen(c.id)}
                    className={`rounded-lg border border-[#1e3d3a]/12 bg-white p-2.5 cursor-grab active:cursor-grabbing hover:border-[#c9a961] transition-colors ${dragId === c.id ? "opacity-50" : ""}`}>
                    <div className="flex items-start justify-between gap-1.5">
                      <h4 className="text-sm font-medium text-[#1e3d3a] leading-tight">{c.name}</h4>
                      {c.converted_lead_id && <ArrowUpRight className="h-3.5 w-3.5 text-[#4d5a24] shrink-0" title="In Leads" />}
                    </div>
                    {c.industry && <p className="text-[11px] text-[#1e3d3a]/45 truncate">{c.industry}</p>}
                    {c.next_followup_at && <p className={`mt-1.5 text-[11px] flex items-center gap-1 ${isOverdue(c.next_followup_at) ? "text-[#b23a48] font-medium" : "text-[#1e3d3a]/55"}`}><CalendarClock className="h-3 w-3" />{fmtDate(c.next_followup_at)}</p>}
                    {c.next_step_owner_name && <p className="mt-0.5 text-[11px] text-[#1e3d3a]/50 flex items-center gap-1"><User className="h-3 w-3" />{c.next_step_owner_name}</p>}
                  </div>
                );
              })}
              {items.length === 0 && <div className="text-center text-[11px] text-[#1e3d3a]/30 py-4">Drop here</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CompanyModal({ company, team, onClose, onChange, onDeleted, onLeadsChanged }) {
  const id = company.id;
  const [c, setC] = useState(company);
  const [busy, setBusy] = useState("");
  useEffect(() => { setC(company); }, [company]);

  const apply = (updated) => { setC(updated); onChange(updated); };

  const patch = async (fields) => {
    try { const { data } = await api.put(`/admin/outreach/companies/${id}`, fields); apply(data); }
    catch { toast.error("Couldn't save the change."); }
  };
  // local edit buffer for text fields; commit on blur if changed
  const [buf, setBuf] = useState(company);
  useEffect(() => { setBuf(company); }, [company]);
  const tf = (name) => ({
    value: buf[name] ?? "",
    onChange: (e) => setBuf((b) => ({ ...b, [name]: e.target.value })),
    onBlur: () => { if ((buf[name] ?? "") !== (c[name] ?? "")) patch({ [name]: buf[name] }); },
  });

  const convert = async () => {
    setBusy("convert");
    try {
      const { data } = await api.post(`/admin/outreach/companies/${id}/convert`);
      toast.success(data.already ? "Already in your Leads pipeline." : "Added to Leads — contacts & history kept here.");
      apply({ ...c, stage: "qualified", converted_lead_id: data.lead_id, converted_at: c.converted_at || new Date().toISOString() });
      onLeadsChanged && onLeadsChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't convert to a lead."); }
    finally { setBusy(""); }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${c.name} and its history? This can't be undone.`)) return;
    setBusy("del");
    try { await api.delete(`/admin/outreach/companies/${id}`); toast.success("Company deleted."); onDeleted(); }
    catch { toast.error("Couldn't delete."); setBusy(""); }
  };

  const logAttempt = async () => {
    try { const { data } = await api.post(`/admin/outreach/companies/${id}/log-attempt`, {}); apply(data); toast.success("Outreach attempt logged."); }
    catch { toast.error("Couldn't log the attempt."); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 py-8" data-testid="outreach-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-3xl rounded-2xl bg-[#f7f5ef] shadow-xl">
        {/* header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 rounded-t-2xl bg-[#1e3d3a] px-5 py-4">
          <div className="min-w-0 flex-1">
            <input {...tf("name")} data-testid="outreach-name"
              className="w-full bg-transparent text-white font-head text-lg focus:outline-none border-b border-transparent focus:border-[#c9a961]/50" />
            <div className="mt-2 flex items-center gap-2">
              <select value={c.stage} onChange={(e) => patch({ stage: e.target.value })} data-testid="outreach-stage-select"
                className="rounded-md bg-white/10 text-white text-xs px-2 py-1 focus:outline-none [&>option]:text-[#1e3d3a]">
                {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
              </select>
              {c.converted_lead_id && <span className="inline-flex items-center gap-1 text-[11px] text-[#d8e0b8] bg-white/10 px-2 py-1 rounded-md"><CheckCircle2 className="h-3.5 w-3.5" /> In Leads</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {c.stage !== "not_interested" && (
              <button onClick={convert} disabled={!!busy} data-testid="outreach-convert"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-3 py-1.5 text-sm font-semibold text-[#1e3d3a] hover:bg-[#b89550] disabled:opacity-50">
                {busy === "convert" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                {c.converted_lead_id ? "In Leads" : "Qualify → Leads"}
              </button>
            )}
            <button onClick={onClose} data-testid="outreach-close" className="rounded-full p-1.5 text-white/70 hover:bg-white/10"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* company info */}
          <section>
            <SectionTitle icon={Building2}>Company information</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className={labelCls}><Target className="inline h-3 w-3 mr-1" />Industry</label><input {...tf("industry")} data-testid="outreach-industry" className={inputCls} /></div>
              <div><label className={labelCls}><Globe className="inline h-3 w-3 mr-1" />Website</label><input {...tf("website")} data-testid="outreach-website" className={inputCls} /></div>
              <div><label className={labelCls}><Phone className="inline h-3 w-3 mr-1" />General phone</label><input {...tf("phone")} data-testid="outreach-phone" className={inputCls} /></div>
              <div><label className={labelCls}><MapPin className="inline h-3 w-3 mr-1" />Address</label><input {...tf("address")} data-testid="outreach-address" className={inputCls} /></div>
            </div>
          </section>

          {/* revenue + health */}
          <section className="grid sm:grid-cols-2 gap-4">
            <div>
              <SectionTitle>Estimated monthly revenue</SectionTitle>
              <select value={c.est_monthly_revenue || "unknown"} onChange={(e) => patch({ est_monthly_revenue: e.target.value })} data-testid="outreach-revenue" className={inputCls}>
                {REVENUE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <label className="mt-2 flex items-center gap-2 text-xs text-[#1e3d3a]/70">
                <input type="checkbox" checked={!!c.revenue_confirmed} onChange={(e) => patch({ revenue_confirmed: e.target.checked })} data-testid="outreach-revenue-confirmed" />
                Confirmed {!c.revenue_confirmed && c.est_monthly_revenue !== "unknown" && <span className="text-[#a3823f]">· estimated</span>}
              </label>
            </div>
            <div>
              <SectionTitle>Business health score</SectionTitle>
              <select value={c.health_rating || "not_assessed"} onChange={(e) => patch({ health_rating: e.target.value })} data-testid="outreach-health" className={inputCls}>
                {HEALTH.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <textarea {...tf("health_notes")} rows={2} placeholder="Supporting notes…" data-testid="outreach-health-notes" className={`${inputCls} mt-2 resize-none`} />
            </div>
          </section>

          {/* dates */}
          <section>
            <SectionTitle icon={Calendar}>Dates &amp; timeline</SectionTitle>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><label className={labelCls}>Initial contact</label><input type="date" value={(c.initial_contact_at || "").slice(0, 10)} onChange={(e) => patch({ initial_contact_at: e.target.value || null })} data-testid="outreach-initial-date" className={inputCls} /></div>
              <div><label className={labelCls}>Next follow-up</label><input type="date" value={(c.next_followup_at || "").slice(0, 10)} onChange={(e) => patch({ next_followup_at: e.target.value || null })} data-testid="outreach-followup-date" className={inputCls} /></div>
              <div><label className={labelCls}>Meeting scheduled</label><input type="date" value={(c.meeting_at || "").slice(0, 10)} onChange={(e) => patch({ meeting_at: e.target.value || null })} data-testid="outreach-meeting-date" className={inputCls} /></div>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-[#1e3d3a]/55">
              <PhoneCall className="h-3.5 w-3.5" /> {(c.outreach_attempts || []).length} outreach attempt{(c.outreach_attempts || []).length === 1 ? "" : "s"} logged
              <button onClick={logAttempt} data-testid="outreach-log-attempt" className="ml-1 rounded-full border border-[#1e3d3a]/15 px-2 py-0.5 text-[11px] hover:border-[#c9a961]">+ Log attempt now</button>
            </div>
          </section>

          {/* next step */}
          <section>
            <SectionTitle icon={Target}>Next step &amp; owner</SectionTitle>
            <div className="grid sm:grid-cols-[1fr_200px] gap-3">
              <div><label className={labelCls}>What needs to happen next</label><input {...tf("next_step")} data-testid="outreach-next-step" placeholder="e.g. Call Maria Tuesday AM" className={inputCls} /></div>
              <div><label className={labelCls}>Who's handling it</label>
                <select value={c.next_step_owner_id || ""} data-testid="outreach-next-owner" className={inputCls}
                  onChange={(e) => { const m = team.find((t) => t.id === e.target.value); patch({ next_step_owner_id: e.target.value || null, next_step_owner_name: m ? m.name : "" }); }}>
                  <option value="">Unassigned</option>
                  {team.map((m) => <option key={m.id} value={m.id}>{m.name}{m.role === "founder" ? " (Founder)" : ""}</option>)}
                </select>
              </div>
            </div>
          </section>

          <ContactsSection company={c} onApply={apply} />
          <ConversationsSection company={c} onApply={apply} />

          <div className="pt-2 border-t border-[#1e3d3a]/10">
            <button onClick={del} disabled={!!busy} data-testid="outreach-delete" className="inline-flex items-center gap-1.5 text-sm text-[#b23a48] hover:underline disabled:opacity-50">
              {busy === "del" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete this company
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return <h4 className="flex items-center gap-1.5 text-[#1e3d3a] font-semibold text-sm mb-2">{Icon && <Icon className="h-4 w-4 text-[#c9a961]" />}{children}</h4>;
}

function ContactsSection({ company, onApply }) {
  const id = company.id;
  const contacts = company.contacts || [];
  const [form, setForm] = useState({ name: "", position: "", email: "", phone: "", is_decision_maker: false, is_gatekeeper: false, notes: "" });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!form.name.trim()) { toast.error("Contact name is required."); return; }
    setBusy(true);
    try { const { data } = await api.post(`/admin/outreach/companies/${id}/contacts`, form); onApply(data); setForm({ name: "", position: "", email: "", phone: "", is_decision_maker: false, is_gatekeeper: false, notes: "" }); }
    catch { toast.error("Couldn't add contact."); }
    finally { setBusy(false); }
  };
  const editField = async (kid, field, value) => {
    try { const { data } = await api.put(`/admin/outreach/companies/${id}/contacts/${kid}`, { [field]: value }); onApply(data); }
    catch { toast.error("Couldn't update contact."); }
  };
  const remove = async (kid) => {
    if (!window.confirm("Remove this contact?")) return;
    try { const { data } = await api.delete(`/admin/outreach/companies/${id}/contacts/${kid}`); onApply(data); }
    catch { toast.error("Couldn't remove contact."); }
  };

  return (
    <section data-testid="outreach-contacts-section">
      <SectionTitle icon={Users}>Contacts ({contacts.length})</SectionTitle>
      <div className="space-y-2">
        {contacts.map((k) => (
          <div key={k.id} className="rounded-lg border border-[#1e3d3a]/12 bg-white p-3" data-testid={`outreach-contact-${k.id}`}>
            <div className="grid sm:grid-cols-2 gap-2">
              <input defaultValue={k.name} onBlur={(e) => e.target.value !== k.name && editField(k.id, "name", e.target.value)} placeholder="Name" className={inputCls} />
              <input defaultValue={k.position} onBlur={(e) => e.target.value !== k.position && editField(k.id, "position", e.target.value)} placeholder="Position" className={inputCls} />
              <input defaultValue={k.email} onBlur={(e) => e.target.value !== k.email && editField(k.id, "email", e.target.value)} placeholder="Email" className={inputCls} />
              <input defaultValue={k.phone} onBlur={(e) => e.target.value !== k.phone && editField(k.id, "phone", e.target.value)} placeholder="Phone" className={inputCls} />
            </div>
            <input defaultValue={k.notes} onBlur={(e) => e.target.value !== (k.notes || "") && editField(k.id, "notes", e.target.value)} placeholder="Notes about this contact…" className={`${inputCls} mt-2`} />
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[#1e3d3a]/70">
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!k.is_decision_maker} onChange={(e) => editField(k.id, "is_decision_maker", e.target.checked)} data-testid={`outreach-dm-${k.id}`} /><Star className="h-3.5 w-3.5 text-[#c9a961]" /> Decision-maker</label>
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!k.is_gatekeeper} onChange={(e) => editField(k.id, "is_gatekeeper", e.target.checked)} /><ShieldQuestion className="h-3.5 w-3.5" /> Can connect us</label>
              <button onClick={() => remove(k.id)} data-testid={`outreach-contact-remove-${k.id}`} className="ml-auto text-[#b23a48] hover:underline inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-lg border border-dashed border-[#1e3d3a]/20 bg-white/60 p-3" data-testid="outreach-add-contact-form">
        <div className="grid sm:grid-cols-2 gap-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name *" data-testid="outreach-new-contact-name" className={inputCls} />
          <input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Position" className={inputCls} />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className={inputCls} />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[#1e3d3a]/70">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.is_decision_maker} onChange={(e) => setForm({ ...form, is_decision_maker: e.target.checked })} /> Decision-maker</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.is_gatekeeper} onChange={(e) => setForm({ ...form, is_gatekeeper: e.target.checked })} /> Can connect us</label>
          <button onClick={add} disabled={busy} data-testid="outreach-add-contact-btn" className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add contact
          </button>
        </div>
      </div>
    </section>
  );
}

function ConversationsSection({ company, onApply }) {
  const id = company.id;
  const convos = company.conversations || [];
  const contacts = company.contacts || [];
  const empty = { channel: "Call", contact_name: "", discussed: "", response: "", needs: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!form.discussed.trim() && !form.response.trim()) { toast.error("Add what was discussed or their response."); return; }
    setBusy(true);
    try { const { data } = await api.post(`/admin/outreach/companies/${id}/conversations`, form); onApply(data); setForm(empty); }
    catch { toast.error("Couldn't log the conversation."); }
    finally { setBusy(false); }
  };
  const remove = async (kid) => {
    if (!window.confirm("Delete this conversation entry?")) return;
    try { const { data } = await api.delete(`/admin/outreach/companies/${id}/conversations/${kid}`); onApply(data); }
    catch { toast.error("Couldn't delete entry."); }
  };

  return (
    <section data-testid="outreach-convos-section">
      <SectionTitle icon={MessageSquare}>Conversation history ({convos.length})</SectionTitle>
      <div className="rounded-lg border border-dashed border-[#1e3d3a]/20 bg-white/60 p-3 mb-3" data-testid="outreach-add-convo-form">
        <div className="grid sm:grid-cols-2 gap-2">
          <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className={inputCls}>
            {["Call", "Email", "Voicemail", "In person", "Text", "LinkedIn", "Other"].map((o) => <option key={o}>{o}</option>)}
          </select>
          <select value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="outreach-convo-contact" className={inputCls}>
            <option value="">Who did you speak with?</option>
            {contacts.map((k) => <option key={k.id} value={k.name}>{k.name}{k.position ? ` — ${k.position}` : ""}</option>)}
            <option value="Unknown / gatekeeper">Unknown / gatekeeper</option>
          </select>
        </div>
        <input value={form.discussed} onChange={(e) => setForm({ ...form, discussed: e.target.value })} placeholder="What was discussed" data-testid="outreach-convo-discussed" className={`${inputCls} mt-2`} />
        <input value={form.response} onChange={(e) => setForm({ ...form, response: e.target.value })} placeholder="Their response" className={`${inputCls} mt-2`} />
        <input value={form.needs} onChange={(e) => setForm({ ...form, needs: e.target.value })} placeholder="Needs / opportunities identified" className={`${inputCls} mt-2`} />
        <button onClick={add} disabled={busy} data-testid="outreach-add-convo-btn" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Log conversation
        </button>
      </div>
      <div className="space-y-2">
        {convos.map((e) => (
          <div key={e.id} className="rounded-lg border border-[#1e3d3a]/12 bg-white p-3 text-sm" data-testid={`outreach-convo-${e.id}`}>
            <div className="flex items-center justify-between gap-2 text-xs text-[#1e3d3a]/50">
              <span>{e.channel}{e.contact_name ? ` · ${e.contact_name}` : ""} · {fmtWhen(e.at)}{e.by_name ? ` · ${e.by_name}` : ""}</span>
              <button onClick={() => remove(e.id)} data-testid={`outreach-convo-delete-${e.id}`} className="text-[#b23a48] hover:underline"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
            {e.discussed && <p className="mt-1 text-[#1e3d3a]"><span className="text-[#1e3d3a]/45">Discussed:</span> {e.discussed}</p>}
            {e.response && <p className="text-[#1e3d3a]"><span className="text-[#1e3d3a]/45">Response:</span> {e.response}</p>}
            {e.needs && <p className="text-[#1e3d3a]"><span className="text-[#1e3d3a]/45">Needs:</span> {e.needs}</p>}
          </div>
        ))}
        {convos.length === 0 && <p className="text-xs text-[#1e3d3a]/45 text-center py-3">No conversations logged yet.</p>}
      </div>
    </section>
  );
}
